import { Box, Button, Grid, Tab, Tabs, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { HOST_API_URL } from '../../../config-global';
import { useDispatch, useSelector } from '../../../redux/store';
import { DateRangePickerSelect } from '../../../reusable-components/date-range-picker';
import Iconify from '../../../reusable-components/iconify';
import { fCurrency } from '../../../utils/formatNumber';
import { isValidDate } from '../../../utils/validate';
import ProcessOrdersButton from './order/ProcessOrdersButton';
import { getPendingOrders, updateOrderSelectType, updateSelectedPendingOrders } from '../../../redux/slices/orders';
import axiosInstance from '../../../utils/axios';
import { objectToFormData } from '../../../api/utils';
import { markMetricRemoved, NotificationMetricType } from '../../../redux/slices/notifications';

CustomToolbar.propTypes = {
  isFiltered: PropTypes.func,
  activeRows: PropTypes.array,
  onStartCombineOrders: PropTypes.func,
  onCombineOrdersCancel: PropTypes.func,
  setSubmittingCombiningOrders: PropTypes.func,
  submittingCombiningOrders: PropTypes.bool,
};

export default function CustomToolbar({
  isFiltered,
  activeRows,
  onStartCombineOrders = null,
  onCombineOrdersCancel = null,
  setSubmittingCombiningOrders = null,
  submittingCombiningOrders = false,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const startDate = searchParams.get('start');
  const endDate = searchParams.get('end');
  const { unreadOrders, selectedPendingOrders, orderSelectType } = useSelector((state) => state.orders);
  const isViewByVendor = searchParams.get('viewByVendor') === '1';

  const handleTabChange = (event, newValue) => {
    switch (newValue) {
      case 0:
        navigate(`/dashboard/orders?filter=pending`);
        break;
      case 1:
        navigate(
          `/dashboard/orders?filter=previous&start=${new Date(
            new Date().setDate(new Date().getDate() - 29)
          ).toISOString()}&end=${new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString()}`
        );
        break;
      default:
        break;
    }
  };

  const downloadPrevOrders = () => {
    const resolvedStart = isValidDate(startDate)
      ? new Date(startDate).toISOString().split('T')[0]
      : new Date(new Date().setDate(new Date().getDate() - 29)).toISOString();
    const resolvedEnd = isValidDate(endDate) ? new Date(endDate) : new Date();
    resolvedEnd.setHours(23, 59, 0, 0);

    const endpoint = isViewByVendor ? 'DownloadPreviousOrdersByVendor' : 'DownloadPreviousOrders';
    const query = new URLSearchParams({
      Start: resolvedStart,
      End: resolvedEnd.toISOString(),
    });

    window.open(`${HOST_API_URL}Api/${endpoint}?${query.toString()}`, '_blank');
  };
  const downloadPendingOrders = () => window.open(`${HOST_API_URL}Api/DownloadPendingOrders`, '_blank');

  const combineOrders = () => {
    setSubmittingCombiningOrders(true);
    axiosInstance
      .post(
        '/CombineOrders',
        objectToFormData({ originalOrderId: selectedPendingOrders[0], combineOrders: selectedPendingOrders.slice(1) })
      )
      .then((res) => {
        if (!res.data?.error) {
          enqueueSnackbar(res.data?.message, { variant: 'success' });
          dispatch(markMetricRemoved(NotificationMetricType.OrdersSubmitted, selectedPendingOrders.slice(1)));
          dispatch(markMetricRemoved(NotificationMetricType.OrdersNeedApproval, selectedPendingOrders.slice(1)));
          dispatch(getPendingOrders());
          dispatch(updateSelectedPendingOrders([]));
          dispatch(updateOrderSelectType(''));
        } else enqueueSnackbar(res.data?.message ?? 'Error combining orders', { variant: 'error' });
      })
      .catch(() => enqueueSnackbar('Error combining orders', { variant: 'error' }))
      .finally(() => setSubmittingCombiningOrders(false));
  };

  return (
    <>
      <Grid container direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: -2 }}>
        <Box>
          <Box sx={{ width: '25vw' }}>
            <Tabs value={isFiltered('pending') ? 0 : 1} onChange={handleTabChange}>
              <Tab label={`Pending Orders${unreadOrders > 0 ? ` (${unreadOrders})` : ''}`} />
              <Tab label="Completed Orders" />
            </Tabs>
          </Box>
        </Box>
      </Grid>

      {orderSelectType !== 'Combine Orders' ? (
        <Grid container alignItems="flex-end" justifyContent="flex-start">
          {isFiltered('pending') && (
            <>
              <ProcessOrdersButton />
              {orderSelectType !== 'Select Orders to Process' && (
                <Box sx={{ display: 'inline-block', ml: 2 }}>
                  <Button
                    color="secondary"
                    variant="outlined"
                    size="small"
                    onClick={onStartCombineOrders}
                    startIcon={<MergeTypeIcon />}
                  >
                    Combine Orders
                  </Button>
                </Box>
              )}
            </>
          )}
          {!isFiltered('pending') && (
            <>
              <Grid item>
                <DateRangePickerSelect showAllTime={!isViewByVendor} />
              </Grid>
              <Grid item>
                <Box sx={{ display: 'inline-block', ml: 2 }}>
                  <Button
                    variant={isViewByVendor ? 'contained' : 'outlined'}
                    size="small"
                    color="secondary"
                    onClick={() => {
                      setSearchParams(
                        (prev) => {
                          prev.set('viewByVendor', isViewByVendor ? '0' : '1');
                          return prev;
                        },
                        { replace: true }
                      );
                    }}
                    startIcon={<Iconify icon="eva:list-fill" />}
                  >
                    View Orders By {isViewByVendor ? 'Order #' : 'Vendor'}
                  </Button>
                </Box>
              </Grid>
            </>
          )}
          {isFiltered('pending') ? (
            <Grid item>
              {orderSelectType !== 'Select Orders to Process' && (
                <Box sx={{ display: 'inline-block', ml: 2 }}>
                  <Button
                    color="secondary"
                    variant="outlined"
                    size="small"
                    onClick={downloadPendingOrders}
                    startIcon={<Iconify icon="file-icons:microsoft-excel" />}
                  >
                    Download Pending Orders
                  </Button>
                </Box>
              )}
            </Grid>
          ) : (
            <Grid item>
              <Box sx={{ display: 'inline-block', ml: 2 }}>
                <Button
                  color="secondary"
                  variant="outlined"
                  size="small"
                  onClick={downloadPrevOrders}
                  startIcon={<Iconify icon="file-icons:microsoft-excel" />}
                >
                  Download Completed Orders
                </Button>
              </Box>
            </Grid>
          )}
          <Grid item sx={{ ml: 2 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                Total
              </Typography>
              <Typography variant="subtitle1" sx={{ color: 'info.main' }}>
                {fCurrency(
                  activeRows
                    .filter((item) => item.deleteFlag !== true)
                    .map((item) => item.total)
                    .reduce((a, b) => a + b, 0)
                )}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      ) : (
        <Box mt={2}>
          <LoadingButton
            loading={submittingCombiningOrders}
            color="secondary"
            variant="outlined"
            size="small"
            onClick={combineOrders}
            disabled={!selectedPendingOrders || selectedPendingOrders.length < 1}
            startIcon={<MergeTypeIcon />}
          >
            Combine Orders
          </LoadingButton>
          <Button sx={{ ml: 1 }} onClick={onCombineOrdersCancel} variant="outlined" color="error" size="small">
            Cancel
          </Button>
        </Box>
      )}
    </>
  );
}
